import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../util/axiosBaseQuery";
import { OrderId } from "../../types/order";

// Backend contract (finish handlers — see
// docs/plans/rental-return-photo-persistence.md): when a return photo was
// submitted but could not be persisted (S3 upload or DB write failed), the
// finish still succeeds and the response carries `photoUploadFailed: true`,
// so the app can tell the customer the photo was not saved instead of
// silently dropping it.
export interface FinishRentalViaTokenResponse {
  orderId: string;
  orderItems: string[];
  photoUploadFailed?: boolean;
}

export const rentalsApiSlice = createApi({
  reducerPath: "rentalsApiSlice",
  baseQuery: axiosBaseQuery(),
  // No query in this slice provides a tag (rental start/finish return the
  // updated order directly and are consumed via the token pages / redux flows),
  // so this slice declares no tags rather than invalidating dead ones.
  tagTypes: [],
  endpoints: (builder) => ({
    startRental: builder.mutation({
      query: ({ orderId, items }: { orderId: OrderId; items: string[] }) => {
        return {
          url: `/rental/start`,
          method: "POST",
          data: {
            orderId: orderId,
            orderItems: items,
          },
        };
      },
    }),
    finishRental: builder.mutation({
      query: ({
        orderId,
        items,
        token,
        finishMethod,
        skipped,
      }: {
        orderId: OrderId;
        items: string[];
        token: string;
        finishMethod?: string;
        // True when the customer was offered the return-photo step (SUP) and
        // tapped skip — lets the backend persist 'skipped' (vs a failed upload).
        skipped?: boolean;
      }) => {
        return {
          url: `/rental/finish/${orderId}`,
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            orderId: orderId,
            orderItems: items,
            ...(finishMethod ? { finish_method: finishMethod } : {}),
            ...(skipped ? { skipped: true } : {}),
          },
        };
      },
    }),
    finishRentalViaToken: builder.mutation<
      FinishRentalViaTokenResponse,
      { finishRentalToken: string; photo?: File }
    >({
      query: ({ finishRentalToken, photo }) => {
        if (photo) {
          const formData = new FormData();
          formData.append("photo", photo);
          return {
            url: `/rental/token/finish/${finishRentalToken}`,
            method: "POST",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
          };
        }
        return {
          url: `/rental/token/finish/${finishRentalToken}`,
          method: "POST",
        };
      },
    }),
    updateItemState: builder.mutation({
      query: ({
        orderId,
        items,
        token,
        state,
      }: {
        orderId: OrderId;
        items: string[];
        state: string;
        token?: string;
      }) => {
        return {
          url: `/rental/update-item/${orderId}`,
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            orderId: orderId,
            orderItems: items,
            state: state,
          },
        };
      },
    }),
  }),
});

export const {
  useStartRentalMutation,
  useFinishRentalMutation,
  useFinishRentalViaTokenMutation,
  useUpdateItemStateMutation,
} = rentalsApiSlice;
